import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SectionCard, StatusBadge, formatDateTime, formatNumber } from '../../../components/design-system/admin';
import type { NetworkStats } from '../../../common/hooks/network-admin.types';
import { NETWORK_LINK_STATUSES } from '../../../common/hooks/network-admin.types';
import { BrandStatusBadge, LinkStatusBadge, methodLabel } from './network-parts';
import { formatShare } from './network-helpers';

const LINK_BAR: Record<string, string> = { confirmed: 'bg-success', suggested: 'bg-warning', rejected: 'bg-destructive/70' };

const n = (v: number | undefined | null) => formatNumber(v ?? 0);

/** SurfaceID: is it answering, which model, its thresholds, and how many vectors each catalogue has. */
export function SurfaceIdCard({ stats }: { stats: NetworkStats }) {
    const { t } = useTranslation();
    const s = stats.surfaceId || { ok: false };
    return (
        <SectionCard title={t('ADMIN_NETWORK.SURFACE_ID.TITLE')} description={t('ADMIN_NETWORK.SURFACE_ID.HINT')} testId="surface-id-card">
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <StatusBadge tone={s.ok ? 'success' : 'danger'}>{s.ok ? t('ADMIN_NETWORK.SURFACE_ID.ON') : t('ADMIN_NETWORK.SURFACE_ID.OFF')}</StatusBadge>
                    <span className="text-sm">{s.model || '—'}</span>
                    {s.thresholds && (
                        <span className="text-xs text-muted-foreground tabular-nums">
                            {t('ADMIN_NETWORK.SURFACE_ID.THRESHOLDS', { same: s.thresholds.same.toFixed(2), similar: s.thresholds.similar.toFixed(2) })}
                        </span>
                    )}
                </div>
                {!s.ok && <p className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.SURFACE_ID.OFF_HINT')}</p>}
                {stats.embeddings?.length ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="ps-0">{t('ADMIN_NETWORK.FIELDS.MODEL')}</TableHead>
                                <TableHead>{t('ADMIN_NETWORK.SURFACE_ID.KIND')}</TableHead>
                                <TableHead className="pe-0 text-end">{t('ADMIN_NETWORK.SURFACE_ID.VECTORS')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {stats.embeddings.map((e) => (
                                <TableRow key={`${e.model}-${e.kind}`}>
                                    <TableCell className={cn('ps-0 text-xs', e.model === s.model ? 'text-foreground' : 'text-muted-foreground')}>{e.model}</TableCell>
                                    <TableCell className="text-xs">{t(`ADMIN_NETWORK.SURFACE_ID.KIND_${e.kind === 'network' ? 'NETWORK' : 'PRODUCT'}`)}</TableCell>
                                    <TableCell className="pe-0 text-end tabular-nums">{n(e.count)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.SURFACE_ID.NO_VECTORS')}</p>
                )}
            </div>
        </SectionCard>
    );
}

/** Suggested / confirmed / rejected as one bar, then by method. */
export function LinkBreakdownCard({ stats }: { stats: NetworkStats }) {
    const { t } = useTranslation();
    const l = stats.links;
    const total = l?.total || 0;
    const methods = Array.from(new Set([...Object.keys(l?.confirmedByMethod || {}), ...Object.keys(l?.suggestedByMethod || {})]));
    return (
        <SectionCard title={t('ADMIN_NETWORK.LINKS.TITLE')} description={t('ADMIN_NETWORK.LINKS.HINT', { total: n(total) })} testId="link-breakdown">
            <div className="flex flex-col gap-3">
                <div className="flex h-2 overflow-hidden rounded-full bg-muted" role="img" aria-label={t('ADMIN_NETWORK.LINKS.TITLE')}>
                    {total > 0 &&
                        NETWORK_LINK_STATUSES.map((s) => {
                            const v = l.byStatus?.[s] || 0;
                            return v ? <div key={s} className={LINK_BAR[s]} style={{ width: `${(v / total) * 100}%` }} /> : null;
                        })}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {NETWORK_LINK_STATUSES.map((s) => (
                        <span key={s} className="inline-flex items-center gap-1.5">
                            <LinkStatusBadge status={s} />
                            <span className="text-sm tabular-nums">{n(l?.byStatus?.[s])}</span>
                        </span>
                    ))}
                </div>
                {methods.length > 0 && (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="ps-0">{t('ADMIN_NETWORK.FIELDS.METHOD')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_NETWORK.LINK_STATUS.CONFIRMED')}</TableHead>
                                <TableHead className="pe-0 text-end">{t('ADMIN_NETWORK.LINK_STATUS.SUGGESTED')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {methods.map((m) => (
                                <TableRow key={m}>
                                    <TableCell className="ps-0">{methodLabel(t, m)}</TableCell>
                                    <TableCell className="text-end tabular-nums">{n(l.confirmedByMethod?.[m])}</TableCell>
                                    <TableCell className="pe-0 text-end tabular-nums">{n(l.suggestedByMethod?.[m])}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
        </SectionCard>
    );
}

/** What people decided on the matcher's suggestions — matching quality as production measures it. */
export function ReviewAcceptanceCard({ stats }: { stats: NetworkStats }) {
    const { t } = useTranslation();
    const r = stats.review;
    const rows = Object.entries(r?.byMethod || {});
    return (
        <SectionCard
            title={t('ADMIN_NETWORK.REVIEW_STATS.TITLE')}
            description={t('ADMIN_NETWORK.REVIEW_STATS.HINT', { decided: n(r?.decided), confirmed: n(r?.confirmed), rejected: n(r?.rejected) })}
            testId="review-acceptance"
        >
            {rows.length ? (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="ps-0">{t('ADMIN_NETWORK.FIELDS.METHOD')}</TableHead>
                            <TableHead className="text-end">{t('ADMIN_NETWORK.LINK_STATUS.CONFIRMED')}</TableHead>
                            <TableHead className="text-end">{t('ADMIN_NETWORK.LINK_STATUS.REJECTED')}</TableHead>
                            <TableHead className="pe-0 text-end">{t('ADMIN_NETWORK.REVIEW_STATS.ACCEPTANCE')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map(([m, v]) => (
                            <TableRow key={m}>
                                <TableCell className="ps-0">{methodLabel(t, m)}</TableCell>
                                <TableCell className="text-end tabular-nums">{n(v.confirmed)}</TableCell>
                                <TableCell className="text-end tabular-nums">{n(v.rejected)}</TableCell>
                                <TableCell className="pe-0 text-end font-medium tabular-nums">{formatShare(v.acceptance)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <p className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.REVIEW_STATS.NONE')}</p>
            )}
        </SectionCard>
    );
}

/** Each brand: how much of it is in the network, digitized, published, and carried by shops. */
export function CoverageTable({ stats }: { stats: NetworkStats }) {
    const { t } = useTranslation();
    const rows = stats.coverage || [];
    return (
        <SectionCard title={t('ADMIN_NETWORK.COVERAGE.TITLE')} description={t('ADMIN_NETWORK.COVERAGE.HINT')} flush testId="coverage-table">
            {rows.length ? (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="ps-6">{t('ADMIN_NETWORK.FIELDS.BRAND')}</TableHead>
                                <TableHead>{t('ADMIN_NETWORK.FIELDS.STATUS')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.COLLECTIONS')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.PRODUCTS')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.PUBLISHED')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.DIGITIZED')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.CARRIED')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.RETAILERS')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_NETWORK.LINK_STATUS.CONFIRMED')}</TableHead>
                                <TableHead className="pe-6 text-end">{t('ADMIN_NETWORK.LINK_STATUS.SUGGESTED')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((c) => (
                                <TableRow key={c.brandId}>
                                    <TableCell className="ps-6 font-medium">
                                        <Link to={`/network/brands/${c.brandId}`} className="hover:underline">{c.name}</Link>
                                    </TableCell>
                                    <TableCell><BrandStatusBadge status={c.status as any} /></TableCell>
                                    <TableCell className="text-end tabular-nums">{n(c.collections)}</TableCell>
                                    <TableCell className="text-end tabular-nums">{n(c.products)}</TableCell>
                                    <TableCell className="text-end tabular-nums">{n(c.published)}</TableCell>
                                    <TableCell className="text-end tabular-nums">
                                        {n(c.digitized)}
                                        <span className="ms-1 text-xs text-muted-foreground">{c.products ? formatShare(c.digitized / c.products) : ''}</span>
                                    </TableCell>
                                    <TableCell className="text-end tabular-nums">{n(c.carried)}</TableCell>
                                    <TableCell className="text-end tabular-nums">{n(c.retailers)}</TableCell>
                                    <TableCell className="text-end tabular-nums">{n(c.confirmedLinks)}</TableCell>
                                    <TableCell className="pe-6 text-end tabular-nums">
                                        {c.suggestedLinks ? <Link to={`/network/review?brandId=${c.brandId}`} className="hover:underline">{n(c.suggestedLinks)}</Link> : n(0)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <p className="px-6 pb-6 text-sm text-muted-foreground">{t('ADMIN_NETWORK.COVERAGE.NONE')}</p>
            )}
        </SectionCard>
    );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className="font-heading text-2xl leading-none font-medium tracking-tight tabular-nums">{value}</span>
            {hint && <span className="text-xs text-muted-foreground tabular-nums">{hint}</span>}
        </div>
    );
}

/** The latest rig evaluation against the ground truth (scripts/network-match-eval.ts). Hidden when there is none. */
export function LastEvalCard({ stats }: { stats: NetworkStats }) {
    const { t } = useTranslation();
    const e = stats.lastEval;
    if (!e) return null;
    const methods = Object.entries(e.byMethod || {});
    const retailers = Object.entries(e.byRetailer || {});
    return (
        <SectionCard
            title={t('ADMIN_NETWORK.EVAL.TITLE')}
            description={t('ADMIN_NETWORK.EVAL.HINT', {
                at: formatDateTime(e.at),
                model: e.model || '—',
                visual: e.visual ? t('ADMIN_NETWORK.EVAL.VISUAL_ON') : t('ADMIN_NETWORK.EVAL.VISUAL_OFF'),
            })}
            testId="last-eval"
        >
            <div className="flex flex-col gap-5">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <Figure label={t('ADMIN_NETWORK.EVAL.PRECISION')} value={formatShare(e.overall?.precision)} hint={t('ADMIN_NETWORK.EVAL.TP_FP', { tp: n(e.overall?.tp), fp: n(e.overall?.fp) })} />
                    <Figure label={t('ADMIN_NETWORK.EVAL.RECALL')} value={formatShare(e.overall?.recall)} hint={t('ADMIN_NETWORK.EVAL.FN', { fn: n(e.overall?.fn) })} />
                    <Figure label={t('ADMIN_NETWORK.EVAL.F1')} value={formatShare(e.overall?.f1)} />
                    <Figure
                        label={t('ADMIN_NETWORK.EVAL.AUTO_CONFIRMED')}
                        value={formatShare(e.autoConfirmed?.precision)}
                        hint={t('ADMIN_NETWORK.EVAL.AUTO_CONFIRMED_HINT', { count: e.autoConfirmed?.count ?? 0 })}
                    />
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {methods.length > 0 && (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="ps-0">{t('ADMIN_NETWORK.FIELDS.METHOD')}</TableHead>
                                    <TableHead className="text-end">{t('ADMIN_NETWORK.EVAL.LINKS')}</TableHead>
                                    <TableHead className="pe-0 text-end">{t('ADMIN_NETWORK.EVAL.PRECISION')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {methods.map(([m, v]) => (
                                    <TableRow key={m}>
                                        <TableCell className="ps-0">{methodLabel(t, m)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{n(v.links)}</TableCell>
                                        <TableCell className="pe-0 text-end tabular-nums">{formatShare(v.precision)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {retailers.length > 0 && (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="ps-0">{t('ADMIN_NETWORK.FIELDS.RETAILER')}</TableHead>
                                    <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.PRODUCTS')}</TableHead>
                                    <TableHead className="text-end">{t('ADMIN_NETWORK.EVAL.PRECISION')}</TableHead>
                                    <TableHead className="pe-0 text-end">{t('ADMIN_NETWORK.EVAL.RECALL')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {retailers.map(([name, v]) => (
                                    <TableRow key={name}>
                                        <TableCell className="ps-0">{name}</TableCell>
                                        <TableCell className="text-end tabular-nums">{n(v.products)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatShare(v.precision)}</TableCell>
                                        <TableCell className="pe-0 text-end tabular-nums">{formatShare(v.recall)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>
        </SectionCard>
    );
}
